const maximumImageSetNesting = 128;

interface CssToken {
    readonly end: number;
    readonly value: string;
}

interface ParsedURLFunction {
    readonly end: number;
    readonly value: string | undefined;
}

/** Maps CSS URL tokens without changing comments or ordinary string content. */
export function mapCSSMediaURLs(css: string, mapURL: (source: string) => string): string {
    return rewriteRange(css, 0, css.length, mapURL, 0);
}

function rewriteRange(
    css: string,
    start: number,
    end: number,
    mapURL: (source: string) => string,
    imageSetDepth: number
): string {
    if (imageSetDepth > maximumImageSetNesting) return css.slice(start, end);
    let output = "";
    let copiedThrough = start;
    let index = start;
    while (index < end) {
        if (isCommentStart(css, index)) {
            index = skipComment(css, index, end);
            continue;
        }
        if (css[index] === "'" || css[index] === '"') {
            index = skipString(css, index, end);
            continue;
        }

        const identifier = parseIdentifier(css, index, end);
        if (identifier === undefined) {
            index++;
            continue;
        }
        const isFunction = css[identifier.end] === "(" && isIdentifierBoundary(css, index);
        if (!isFunction) {
            index = identifier.end;
            continue;
        }

        const name = identifier.value.toLowerCase();
        if (name === "url") {
            const parsed = parseURLFunction(css, identifier.end, end);
            if (parsed.value !== undefined) {
                const source = parsed.value;
                const rewritten = mapURL(source);
                if (rewritten !== source) {
                    output += css.slice(copiedThrough, index);
                    output += `url("${escapeCSSString(rewritten, '"')}")`;
                    copiedThrough = parsed.end;
                }
            }
            if (parsed.end === end) return output + css.slice(copiedThrough, end);
            index = parsed.end;
            continue;
        }

        if (name === "image-set" || name === "-webkit-image-set") {
            const close = findFunctionEnd(css, identifier.end, end);
            const argsStart = identifier.end + 1;
            const argsEnd = close ?? end;
            const rewrittenArgs = rewriteImageSetArguments(css, argsStart, argsEnd, mapURL, imageSetDepth + 1);
            if (rewrittenArgs !== css.slice(argsStart, argsEnd)) {
                output += css.slice(copiedThrough, argsStart);
                output += rewrittenArgs;
                copiedThrough = argsEnd;
            }
            if (close === undefined) return output + css.slice(copiedThrough, end);
            index = close + 1;
            continue;
        }

        index = identifier.end;
    }
    return copiedThrough === start ? css.slice(start, end) : output + css.slice(copiedThrough, end);
}

function rewriteImageSetArguments(
    css: string,
    start: number,
    end: number,
    mapURL: (source: string) => string,
    imageSetDepth: number
): string {
    let output = "";
    let optionStart = start;
    let index = start;
    while (index < end) {
        if (isCommentStart(css, index)) {
            index = skipComment(css, index, end);
            continue;
        }
        if (css[index] === "'" || css[index] === '"') {
            index = skipString(css, index, end);
            continue;
        }
        const identifier = parseIdentifier(css, index, end);
        if (
            identifier?.value.toLowerCase() === "url" &&
            css[identifier.end] === "(" &&
            isIdentifierBoundary(css, index)
        ) {
            index = parseURLFunction(css, identifier.end, end).end;
            continue;
        }
        if (identifier !== undefined) {
            index = identifier.end;
            continue;
        }
        if (css[index] === "(") {
            const close = findFunctionEnd(css, index, end);
            if (close === undefined) break;
            index = close + 1;
            continue;
        }
        if (css[index] === ",") {
            output += rewriteImageSetOption(css, optionStart, index, mapURL, imageSetDepth);
            output += ",";
            optionStart = index + 1;
        }
        index++;
    }
    return output + rewriteImageSetOption(css, optionStart, end, mapURL, imageSetDepth);
}

function rewriteImageSetOption(
    css: string,
    start: number,
    end: number,
    mapURL: (source: string) => string,
    imageSetDepth: number
): string {
    let index = start;
    while (index < end) {
        if (isWhitespace(css[index])) {
            index++;
            continue;
        }
        if (isCommentStart(css, index)) {
            index = skipComment(css, index, end);
            continue;
        }
        break;
    }

    const quote = css[index];
    if (quote !== "'" && quote !== '"') return rewriteRange(css, start, end, mapURL, imageSetDepth);
    const token = parseString(css, index, end);
    if (token === undefined) return rewriteRange(css, start, end, mapURL, imageSetDepth);

    const source = token.value;
    const rewritten = mapURL(source);
    if (rewritten === source) return rewriteRange(css, start, end, mapURL, imageSetDepth);
    return (
        css.slice(start, index) +
        quote +
        escapeCSSString(rewritten, quote) +
        quote +
        rewriteRange(css, token.end, end, mapURL, imageSetDepth)
    );
}

function parseURLFunction(css: string, functionNameEnd: number, end: number): ParsedURLFunction {
    const argumentStart = functionNameEnd + 1;
    let index = skipWhitespace(css, argumentStart, end);
    if (index >= end) return { value: "", end };

    const quote = css[index];
    if (quote === "'" || quote === '"') {
        const token = parseString(css, index, end);
        if (token === undefined) {
            const remainder = css.slice(index + 1, end);
            const close = findURLClose(css, index + 1, end);
            const value =
                close === end && !/[\n\r\f\u0000-\u001f\u007f]/.test(remainder) ? decodeEscapes(remainder) : undefined;
            return { value, end: close };
        }
        const afterString = skipWhitespaceAndComments(css, token.end, end);
        if (css[afterString] === ")") return { value: token.value, end: afterString + 1 };
        if (afterString === end) return { value: token.value, end };
        return { value: undefined, end: findURLClose(css, token.end, end) };
    }

    const valueStart = index;
    let valueEnd = end;
    let sawTrailingWhitespace = false;
    while (index < end) {
        const character = css[index];
        if (character === ")") {
            const sourceEnd = sawTrailingWhitespace ? valueEnd : index;
            return { value: decodeEscapes(css.slice(valueStart, sourceEnd)), end: index + 1 };
        }
        if (isInvalidURLControl(character) || character === "'" || character === '"' || character === "(") {
            return { value: undefined, end: findURLClose(css, index, end) };
        }
        if (character === "\\") {
            const escape = consumeEscape(css, index);
            if (escape === undefined || escape.value === "")
                return { value: undefined, end: findURLClose(css, index, end) };
            if (sawTrailingWhitespace) return { value: undefined, end: findURLClose(css, index, end) };
            index = escape.end;
        } else if (isWhitespace(character)) {
            if (!sawTrailingWhitespace) valueEnd = index;
            sawTrailingWhitespace = true;
            index++;
        } else {
            if (sawTrailingWhitespace) return { value: undefined, end: findURLClose(css, index, end) };
            index++;
        }
    }
    return { value: decodeEscapes(css.slice(valueStart, valueEnd)), end };
}

function findURLClose(css: string, start: number, end: number): number {
    let index = start;
    while (index < end) {
        if (css[index] === ")") return index + 1;
        if (css[index] === "\\") {
            const escape = consumeEscape(css, index);
            index = escape === undefined ? index + 1 : escape.end;
        } else index++;
    }
    return end;
}

function parseString(css: string, start: number, end: number): CssToken | undefined {
    const quote = css[start];
    let index = start + 1;
    while (index < end) {
        if (css[index] === quote) {
            return { value: decodeEscapes(css.slice(start + 1, index)), end: index + 1 };
        }
        if (css[index] === "\n" || css[index] === "\r" || css[index] === "\f") return undefined;
        if (css[index] === "\\") {
            const escape = consumeEscape(css, index);
            if (escape === undefined) return undefined;
            index = escape.end;
        } else {
            index++;
        }
    }
    return undefined;
}

function parseIdentifier(css: string, start: number, end: number): CssToken | undefined {
    let index = start;
    let value = "";
    while (index < end) {
        if (css[index] === "\\") {
            const escape = consumeEscape(css, index);
            if (escape === undefined || escape.value === "") break;
            value += escape.value;
            index = escape.end;
        } else if (isNameCharacter(css[index])) {
            value += css[index++];
        } else {
            break;
        }
    }
    return index === start ? undefined : { value, end: index };
}

function findFunctionEnd(css: string, functionNameEnd: number, end: number): number | undefined {
    let depth = 1;
    let index = functionNameEnd + 1;
    while (index < end) {
        const identifier = parseIdentifier(css, index, end);
        if (
            identifier?.value.toLowerCase() === "url" &&
            css[identifier.end] === "(" &&
            isIdentifierBoundary(css, index)
        ) {
            index = parseURLFunction(css, identifier.end, end).end;
            continue;
        }
        if (identifier !== undefined) {
            index = identifier.end;
            continue;
        }
        if (isCommentStart(css, index)) {
            index = skipComment(css, index, end);
        } else if (css[index] === "'" || css[index] === '"') {
            index = skipString(css, index, end);
        } else if (css[index] === "\\") {
            const escape = consumeEscape(css, index);
            if (escape === undefined) return undefined;
            index = escape.end;
        } else if (css[index] === "(") {
            depth++;
            index++;
        } else if (css[index] === ")") {
            depth--;
            if (depth === 0) return index;
            index++;
        } else {
            index++;
        }
    }
    return undefined;
}

function skipWhitespaceAndComments(css: string, start: number, end: number): number {
    let index = start;
    while (index < end) {
        if (isWhitespace(css[index])) index++;
        else if (isCommentStart(css, index)) index = skipComment(css, index, end);
        else break;
    }
    return index;
}

function skipWhitespace(css: string, start: number, end: number): number {
    let index = start;
    while (index < end && isWhitespace(css[index])) index++;
    return index;
}

function skipComment(css: string, start: number, end: number): number {
    const close = css.indexOf("*/", start + 2);
    return close === -1 || close + 2 > end ? end : close + 2;
}

function skipString(css: string, start: number, end: number): number {
    const quote = css[start];
    let index = start + 1;
    while (index < end) {
        if (css[index] === quote) return index + 1;
        if (css[index] === "\n" || css[index] === "\r" || css[index] === "\f") return index;
        if (css[index] === "\\") {
            const escape = consumeEscape(css, index);
            if (escape === undefined) return end;
            index = escape.end;
        } else index++;
    }
    return end;
}

function consumeEscape(value: string, start: number): CssToken | undefined {
    if (value[start] !== "\\" || start + 1 >= value.length) return undefined;
    const next = value[start + 1];
    if (/[\da-f]/i.test(next)) {
        let end = start + 1;
        while (end < value.length && end < start + 7 && /[\da-f]/i.test(value[end])) end++;
        const codePoint = Number.parseInt(value.slice(start + 1, end), 16);
        if (isWhitespace(value[end])) {
            if (value[end] === "\r" && value[end + 1] === "\n") end += 2;
            else end++;
        }
        return {
            value:
                codePoint === 0 || codePoint > 0x10ffff || (codePoint >= 0xd800 && codePoint <= 0xdfff)
                    ? "\ufffd"
                    : String.fromCodePoint(codePoint),
            end,
        };
    }
    if (next === "\n" || next === "\f") return { value: "", end: start + 2 };
    if (next === "\r") return { value: "", end: value[start + 2] === "\n" ? start + 3 : start + 2 };
    return { value: next, end: start + 2 };
}

function decodeEscapes(value: string): string {
    let result = "";
    let index = 0;
    while (index < value.length) {
        if (value[index] !== "\\") result += value[index++];
        else {
            const escape = consumeEscape(value, index);
            if (escape === undefined) return value;
            result += escape.value;
            index = escape.end;
        }
    }
    return result;
}

function escapeCSSString(value: string, quote: string): string {
    return value
        .replace(/\\/g, "\\\\")
        .replace(new RegExp(quote, "g"), `\\${quote}`)
        .replace(/\n/g, "\\a ")
        .replace(/\r/g, "\\d ")
        .replace(/\f/g, "\\c ");
}

function isCommentStart(css: string, index: number): boolean {
    return css[index] === "/" && css[index + 1] === "*";
}

function isIdentifierBoundary(css: string, index: number): boolean {
    return index === 0 || (!isNameCharacter(css[index - 1]) && css[index - 1] !== "\\");
}

function isNameCharacter(character: string | undefined): boolean {
    if (character === undefined) return false;
    return /[\da-z_-]/i.test(character) || character.charCodeAt(0) >= 0x80;
}

function isWhitespace(character: string | undefined): boolean {
    return character === " " || character === "\t" || character === "\n" || character === "\r" || character === "\f";
}

function isInvalidURLControl(character: string): boolean {
    const code = character.charCodeAt(0);
    return code === 0 || (code >= 1 && code <= 8) || code === 11 || (code >= 14 && code <= 31) || code === 127;
}
